import React, {useMemo} from "react";
import {Bar, BarChart, CartesianGrid, Legend, ResponsiveContainer, Tooltip, XAxis, YAxis} from "recharts";
import ChartCard from "./ChartCard.jsx";
import useStatisticsData from "./useStatisticsData.js";
import {colorForGroup, formatEuro, formatEuroCompact, formatMonthPeriod, OTHER_GROUP_LABEL} from "./format.js";

function pivotByPeriod(rows, colorMap) {
    const map = new Map();
    const periods = [];
    rows.forEach((row) => {
        if (!map.has(row.period)) {
            map.set(row.period, {period: row.period});
            periods.push(row.period);
        }
        const bucket = row.article_group && colorMap[row.article_group] ? row.article_group : OTHER_GROUP_LABEL;
        const entry = map.get(row.period);
        entry[bucket] = (entry[bucket] || 0) + row.net_amount;
    });
    periods.sort();
    return periods.map((period) => map.get(period));
}

/** #4 Umsatzverlauf nach Warengruppe, gestapelt (Balken mit abgerundeten
 * Enden und sichtbarer Lücke zwischen den Segmenten). */
export default function RevenueByGroupChart({filters, colorMap, groupOrder}) {
    const params = useMemo(() => ({...filters, granularity: "month", by_group: 1}), [filters]);
    const {data, loading, error} = useStatisticsData("/statistics/time-series", params);
    const rawRows = data?.rows || [];

    const {pivoted, seriesKeys} = useMemo(() => {
        const pivotedRows = pivotByPeriod(rawRows, colorMap);
        const present = new Set();
        rawRows.forEach((row) => {
            present.add(row.article_group && colorMap[row.article_group] ? row.article_group : OTHER_GROUP_LABEL);
        });
        const keys = (groupOrder || []).filter((group) => present.has(group));
        if (present.has(OTHER_GROUP_LABEL)) {
            keys.push(OTHER_GROUP_LABEL);
        }
        pivotedRows.forEach((entry) => {
            keys.forEach((key) => {
                entry[key] = entry[key] || 0;
            });
        });
        return {pivoted: pivotedRows, seriesKeys: keys};
    }, [rawRows, colorMap, groupOrder]);

    return (
        <ChartCard
            title="Umsatzverlauf nach Warengruppe"
            help="Monatlicher Nettoumsatz, gestapelt nach Warengruppe. Kleinere Warengruppen werden zu „Sonstige“ zusammengefasst; die Farbzuordnung je Warengruppe bleibt über alle Filter hinweg gleich."
            loading={loading}
            error={error}
            empty={!loading && pivoted.length === 0}
            height={300}
        >
            <ResponsiveContainer width="100%" height={300}>
                <BarChart data={pivoted} margin={{top: 8, right: 12, left: 0, bottom: 0}}>
                    <CartesianGrid strokeDasharray="3 3" vertical={false} stroke="var(--bs-border-color, #dee2e6)" />
                    <XAxis dataKey="period" tickFormatter={formatMonthPeriod} tick={{fontSize: 11}} axisLine={false} tickLine={false} />
                    <YAxis tickFormatter={formatEuroCompact} tick={{fontSize: 12}} axisLine={false} tickLine={false} width={70} />
                    <Tooltip formatter={(value, name) => [formatEuro(value), name]} labelFormatter={(label) => formatMonthPeriod(label)} />
                    <Legend wrapperStyle={{fontSize: 12}} />
                    {seriesKeys.map((key) => (
                        <Bar key={key} dataKey={key} name={key} stackId="revenue" fill={colorForGroup(colorMap, key)} radius={[2, 2, 2, 2]} stroke="#fff" strokeWidth={1} />
                    ))}
                </BarChart>
            </ResponsiveContainer>
        </ChartCard>
    );
}
